<template>
  <v-expansion-panels popout>
    <!-- Device -->
    <v-expansion-panel>
      <v-expansion-panel-header>
        <h4><v-icon color="blue">mdi-devices</v-icon> Device</h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <Device v-model="obj.device" @input="changed" />
      </v-expansion-panel-content>
    </v-expansion-panel>
    <!-- Global Vars -->
    <v-expansion-panel>
      <v-expansion-panel-header>
        <h4>
          <Tooltip
            content="You are able to use Plan's environment variables inside steps with ${{}} syntax."
            top
          >
            <v-icon :color="hasEnv ? 'orange' : null">
              mdi-tune-variant
            </v-icon>
            Global Variables
          </Tooltip>
        </h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <AddList
          v-model="obj.env"
          outlined
          changedRef="env"
          emptyMsg="No Environment Variables"
          @input="changed()"
          :fields="[
            { type: 'text', name: 'name', label: 'Name' },
            { type: 'text', name: 'val', label: 'Value' },
          ]"
        />
      </v-expansion-panel-content>
    </v-expansion-panel>
    <!-- Headers -->
    <v-expansion-panel>
      <v-expansion-panel-header>
        <h4>
          <Tooltip
            content="Set default authentication routine - for seamless authentication"
            top
          >
            <v-icon :color="hasAuth ? 'cyan' : null">
              mdi-server-security
            </v-icon>
            Authentication
          </Tooltip>
        </h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row v-if="isRoutine">
          <KnobBtn
            text="Is Auth Method"
            :left="false"
            tooltip="Set default authentication routine - for seamless authentication"
            icon="shield-refresh"
            v-model="obj.isAuthMethod"
          />
        </v-row>
        <v-row v-else>
          <v-col cols="auto">
            <Tooltip content="Select Auth method test routine" top>
              <v-autocomplete
                :items="authMethods"
                prepend-icon="mdi-cog-clockwise"
                label="Authentication Method"
                outlined
                hide-details
                v-model="obj.authMethodId"
                :disabled="obj.skipAuthMethod"
                clearable
                dense
                no-data-text="No Methods (Routines) were found"
              >
              </v-autocomplete>
            </Tooltip>
          </v-col>
          <KnobBtn
            text="Skip Auth Method"
            :left="false"
            tooltip="Set default authentication routine - for seamless authentication"
            icon="shield-refresh"
            v-model="obj.skipAuthMethod"
          />
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <!-- Headers -->
    <v-expansion-panel>
      <v-expansion-panel-header>
        <h4>
          <Tooltip
            content="Inject your custome headers to the Plan's scope."
            top
          >
            <v-icon :color="hasHeaders ? 'purple lighten-2' : null">
              mdi-view-list
            </v-icon>
            Headers
          </Tooltip>
        </h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <RequestHeaders v-model="obj.headers" @input="changed()" />
      </v-expansion-panel-content>
    </v-expansion-panel>
    <!-- Permissions -->
    <v-expansion-panel>
      <v-expansion-panel-header>
        <h4>
          <v-icon
            :color="
              obj.permissions && obj.permissions.length ? 'success' : null
            "
          >
            mdi-shield-lock-open
          </v-icon>
          Allowed Permissions
        </h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <AddList
          v-model="obj.permissions"
          outlined
          changedRef="permissions"
          @changed="changed"
          chips
          emptyMsg="No Additional Permissions"
          :fields="[
            {
              type: 'list',
              name: 'permission',
              label: 'Search Permission',
              items: premissionList,
            },
          ]"
        />
      </v-expansion-panel-content>
    </v-expansion-panel>
    <!-- GEO -->
    <v-expansion-panel>
      <v-expansion-panel-header>
        <h4>
          <v-icon :color="obj.geo && Object.keys(obj.geo).length ? 'red' : null"
            >mdi-map-marker-radius
          </v-icon>
          Geo-Location
        </h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row>
          <v-col>
            <Countries :geo="obj.geo" @changed="changed" />
          </v-col>
          <v-col>
            <Locale :locale="obj.locale" @changed="changed" />
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <!-- RESOURCES -->
    <!-- <v-expansion-panel>
      <v-expansion-panel-header>
        <h4>
          <Tooltip
            content="upload files to be used under the Test Case's scope."
            top
          >
            <v-icon :color="hasResources ? 'cyan lighten-2' : null">
              mdi-folder-sync
            </v-icon>
            Resources
          </Tooltip>
        </h4>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <FileListUpload v-model="obj.resources" @input="changed()" />
      </v-expansion-panel-content>
    </v-expansion-panel> -->
  </v-expansion-panels>
</template>

<script lang="ts">
import { permissions } from "@/utils";
import Vue from "vue";
import VueTypes from "vue-types";
import Device from "./Device.vue";
import AddList from "./AddList.vue";
import Tooltip from "@/components/Tooltip.vue";
import Countries from "./Countries.vue";
import RequestHeaders from "./RequestHeaders.vue";
import Locale from "./Locale.vue";
import KnobBtn from "./buttons/KnobBtn.vue";
import { SelectItem } from "@/types";
// import FileListUpload from "./FileListUpload.vue";

export default Vue.extend({
  name: "EnvSettings",
  data() {
    return {
      selected: undefined,
    };
  },
  props: {
    value: VueTypes.any,
    isRoutine: VueTypes.bool.def(false),
  },
  computed: {
    obj: {
      get(): any {
        return this.value;
      },
      set(val: any) {
        this.changed(val);
      },
    },
    authMethods(): SelectItem[] {
      return this.$store.getters["Plans/getAuthMethods"];
    },
    hasHeaders(): boolean {
      return this.obj.headers && Object.keys(this.obj.headers).length > 0;
    },
    hasAuth(): boolean {
      return (
        this.obj.skipAuthMethod ||
        this.obj.isAuthMethod ||
        this.obj.authMethodId
      );
    },
    hasResources(): boolean {
      return this.obj.resources?.length > 0;
    },
    hasEnv(): boolean {
      return this.obj.env?.length > 0;
    },
    premissionList() {
      return permissions.filter((p) =>
        this.obj.permissions ? this.obj.permissions.indexOf(p) < 0 : true
      );
    },
  },
  methods: {
    changed(val = undefined) {
      this.$emit("input", val);
    },
  },
  components: {
    Device,
    AddList,
    Tooltip,
    Countries,
    RequestHeaders,
    Locale,
    KnobBtn,
  },
});
</script>
<style scoped></style>
